import { useMemo, useRef, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Upload, X, Loader2 } from "lucide-react";
import Logo from "@/components/Logo";
import { api, track, formatApiError, fileUrl } from "@/lib/api";
import { IMAGES } from "@/lib/images";
import { Container, Overline } from "@/components/primitives";

const MARKETS = ["USA", "Canada", "UK", "Europe", "Middle East", "Other"];
const SERVICES = ["Private Transfers", "Boat Tours", "Private Chef", "Wine Experiences", "Wellness", "Grocery Delivery", "Car Rental", "Tours & Excursions", "Babysitting", "Romantic Experiences", "Other"];
const PROPERTY_TYPES = ["Luxury Villa", "Private Estate", "Boutique Residence", "Other"];
const ROLES = ["Owner", "Property Manager", "Hospitality Company", "Other"];

const STEPS = ["Property", "Management", "Guest Profile", "Experience"];

function Field({ label, children, required }) {
  return (
    <label className="block">
      <span className="block text-[10px] tracking-[0.25em] uppercase text-ivory/50 mb-2">
        {label}{required && <span className="text-gold ml-1">·</span>}
      </span>
      {children}
    </label>
  );
}

const inputCls =
  "w-full bg-transparent border-b border-ivory/20 focus:border-gold outline-none py-3 text-ivory font-light placeholder:text-ivory/25 transition-colors duration-500";

function Input(props) {
  return <input {...props} className={inputCls} />;
}
function Select({ options, placeholder, ...props }) {
  return (
    <select {...props} className={`${inputCls} appearance-none cursor-pointer`}>
      <option value="" className="bg-navy-800">{placeholder || "Select"}</option>
      {options.map((o) => <option key={o} value={o} className="bg-navy-800">{o}</option>)}
    </select>
  );
}
function Chips({ options, value, onToggle, testid }) {
  return (
    <div className="flex flex-wrap gap-3" data-testid={testid}>
      {options.map((o) => {
        const active = value.includes(o);
        return (
          <button
            type="button"
            key={o}
            onClick={() => onToggle(o)}
            data-testid={`chip-${o.toLowerCase().replace(/\s+/g, "-")}`}
            className={`px-4 py-2.5 text-xs tracking-wide border transition-colors duration-400 ${
              active ? "border-gold bg-gold/10 text-gold" : "border-ivory/20 text-ivory/60 hover:border-ivory/50"
            }`}
          >
            {o}
          </button>
        );
      })}
    </div>
  );
}

const initForm = {
  property: { name: "", website_url: "", location: "", city: "", bedrooms: "", max_guests: "", property_type: "" },
  management: { contact_name: "", email: "", phone: "", company: "", years_operating: "", properties_managed: "", role: "" },
  guest_profile: { markets: [], avg_guest_profile: "", direct_bookings_pct: "", booking_channels: "" },
  experience: { what_makes_exceptional: "", services: [] },
};

export default function Apply({ shareLinkCode = null, embedded = false } = {}) {
  const location = useLocation();
  const invitation_id = location.state?.invitation_id || null;
  const invitation_source = location.state?.invitation_source || null;

  const [step, setStep] = useState(0);
  const [form, setForm] = useState(initForm);
  const [images, setImages] = useState([]); // [{path, uploading}]
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  const set = (group, key, val) => setForm((f) => ({ ...f, [group]: { ...f[group], [key]: val } }));
  const toggle = (group, key, val) =>
    setForm((f) => {
      const arr = f[group][key];
      return { ...f, [group]: { ...f[group], [key]: arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val] } };
    });

  const stepValid = useMemo(() => {
    if (step === 0) return form.property.name.trim() && form.property.website_url.trim();
    if (step === 1) return form.management.contact_name.trim() && /\S+@\S+\.\S+/.test(form.management.email);
    return true;
  }, [step, form]);

  const uploadFiles = async (files) => {
    const remaining = 10 - images.length;
    const chosen = Array.from(files).slice(0, remaining);
    for (const file of chosen) {
      const tmp = { id: Math.random().toString(36).slice(2), uploading: true };
      setImages((p) => [...p, tmp]);
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setImages((p) => p.map((im) => (im.id === tmp.id ? { id: tmp.id, path: data.path, uploading: false } : im)));
      } catch (err) {
        setImages((p) => p.filter((im) => im.id !== tmp.id));
      }
    }
  };

  const submit = async () => {
    setError("");
    if (!consent) { setError("Please agree to the processing of your data to continue."); return; }
    if (!form.property.website_url.trim()) { setError("The property website / listing URL is required."); return; }
    setSubmitting(true);
    try {
      const payload = {
        property: {
          ...form.property,
          bedrooms: form.property.bedrooms ? Number(form.property.bedrooms) : null,
          max_guests: form.property.max_guests ? Number(form.property.max_guests) : null,
        },
        management: {
          ...form.management,
          years_operating: form.management.years_operating ? Number(form.management.years_operating) : null,
          properties_managed: form.management.properties_managed ? Number(form.management.properties_managed) : null,
        },
        guest_profile: {
          ...form.guest_profile,
          direct_bookings_pct: form.guest_profile.direct_bookings_pct ? Number(form.guest_profile.direct_bookings_pct) : null,
        },
        experience: form.experience,
        images: images.filter((i) => i.path).map((i) => i.path),
        consent,
        invitation_id,
        invitation_source,
        share_link_code: shareLinkCode,
      };
      await api.post("/applications", payload);
      track("application_submitted", { invited: !!invitation_id, share_link: !!shareLinkCode });
      setDone(true);
      window.scrollTo(0, 0);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="relative min-h-[100svh] bg-navy-900 flex flex-col items-center justify-center text-center px-6 py-24 overflow-hidden" data-testid="application-confirmation">
        <img src={IMAGES.sorrentoCliffs} alt="" className="absolute inset-0 w-full h-full object-cover opacity-15" />
        <div className="absolute inset-0 bg-navy-900/85" />
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }} className="relative z-10 max-w-xl">
          <Logo variant="full" className="h-20 mx-auto" />
          <h1 className="mt-14 font-serif font-light text-4xl md:text-6xl text-ivory tracking-tight">Application Received.</h1>
          <p className="mt-8 text-ivory/70 font-light leading-relaxed">
            Thank you for your interest in VillaFlow Private Collection. Your property will now be
            reviewed by our Selection Team. We carefully evaluate every application. If your property
            progresses to the next stage, a member of our team will contact you personally.
          </p>
          <div className="gold-rule my-12 max-w-[100px] mx-auto" />
          <p className="text-ivory/50 text-xs tracking-[0.2em] uppercase">VillaFlow Private Collection</p>
          <p className="font-serif italic text-gold/80 text-lg mt-1">Every Stay. More Value.</p>
          <Link to="/" className="mt-12 inline-block text-ivory/60 hover:text-gold text-xs tracking-[0.2em] uppercase transition-colors" data-testid="confirmation-home-link">Return to VillaFlow</Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className={`relative bg-navy-900 ${embedded ? "pt-14" : "pt-32"} pb-32 min-h-screen`} data-testid="apply-page">
      <Container className="max-w-3xl">
        {!embedded && (
          <>
            <Overline>{invitation_id ? "Invited Application" : "Private Application"}</Overline>
            <h1 className="mt-6 font-serif font-light text-4xl md:text-6xl text-ivory tracking-tight leading-[1.05]">
              Think your property belongs here?
            </h1>
            <p className="mt-6 text-ivory/60 font-light leading-relaxed max-w-2xl">
              VillaFlow Private Collection is intentionally limited. Every property is individually
              reviewed by the VillaFlow Selection Team, based on property standards, hospitality,
              guest experience and compatibility with the Collection.
            </p>
          </>
        )}

        {/* Progress */}
        <div className="mt-14 flex items-center gap-2 md:gap-4" data-testid="apply-steps">
          {STEPS.map((s, i) => (
            <div key={s} className="flex-1">
              <div className={`h-px w-full transition-colors duration-500 ${i <= step ? "bg-gold" : "bg-ivory/15"}`} />
              <span className={`mt-3 block text-[10px] tracking-[0.2em] uppercase transition-colors ${i === step ? "text-gold" : "text-ivory/40"}`}>
                {String(i + 1).padStart(2, "0")} {s}
              </span>
            </div>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -24 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="mt-16"
          >
            {step === 0 && (
              <div className="grid sm:grid-cols-2 gap-x-10 gap-y-8" data-testid="step-property">
                <div className="sm:col-span-2"><Field label="Property name" required><Input value={form.property.name} onChange={(e) => set("property", "name", e.target.value)} data-testid="input-property-name" /></Field></div>
                <div className="sm:col-span-2"><Field label="Property website / listing URL" required><Input placeholder="https://…" value={form.property.website_url} onChange={(e) => set("property", "website_url", e.target.value)} data-testid="input-property-url" /></Field></div>
                <Field label="Location / Region"><Input placeholder="Amalfi Coast" value={form.property.location} onChange={(e) => set("property", "location", e.target.value)} data-testid="input-location" /></Field>
                <Field label="City"><Input placeholder="Positano" value={form.property.city} onChange={(e) => set("property", "city", e.target.value)} data-testid="input-city" /></Field>
                <Field label="Bedrooms"><Input type="number" min="0" value={form.property.bedrooms} onChange={(e) => set("property", "bedrooms", e.target.value)} data-testid="input-bedrooms" /></Field>
                <Field label="Maximum guests"><Input type="number" min="0" value={form.property.max_guests} onChange={(e) => set("property", "max_guests", e.target.value)} data-testid="input-max-guests" /></Field>
                <div className="sm:col-span-2"><Field label="Property type"><Select options={PROPERTY_TYPES} value={form.property.property_type} onChange={(e) => set("property", "property_type", e.target.value)} data-testid="input-property-type" /></Field></div>
              </div>
            )}

            {step === 1 && (
              <div className="grid sm:grid-cols-2 gap-x-10 gap-y-8" data-testid="step-management">
                <Field label="Owner / Property Manager" required><Input value={form.management.contact_name} onChange={(e) => set("management", "contact_name", e.target.value)} data-testid="input-contact-name" /></Field>
                <Field label="Role"><Select options={ROLES} value={form.management.role} onChange={(e) => set("management", "role", e.target.value)} data-testid="input-role" /></Field>
                <Field label="Email" required><Input type="email" value={form.management.email} onChange={(e) => set("management", "email", e.target.value)} data-testid="input-email" /></Field>
                <Field label="Phone"><Input value={form.management.phone} onChange={(e) => set("management", "phone", e.target.value)} data-testid="input-phone" /></Field>
                <Field label="Company"><Input value={form.management.company} onChange={(e) => set("management", "company", e.target.value)} data-testid="input-company" /></Field>
                <Field label="Years operating"><Input type="number" min="0" value={form.management.years_operating} onChange={(e) => set("management", "years_operating", e.target.value)} data-testid="input-years" /></Field>
                <div className="sm:col-span-2"><Field label="Number of properties managed"><Input type="number" min="0" value={form.management.properties_managed} onChange={(e) => set("management", "properties_managed", e.target.value)} data-testid="input-num-properties" /></Field></div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-10" data-testid="step-guest">
                <Field label="Primary guest markets"><div className="mt-3"><Chips options={MARKETS} value={form.guest_profile.markets} onToggle={(v) => toggle("guest_profile", "markets", v)} testid="markets-chips" /></div></Field>
                <Field label="Average guest profile"><Input placeholder="e.g. high-spending international families" value={form.guest_profile.avg_guest_profile} onChange={(e) => set("guest_profile", "avg_guest_profile", e.target.value)} data-testid="input-guest-profile" /></Field>
                <div className="grid sm:grid-cols-2 gap-x-10 gap-y-8">
                  <Field label="Direct bookings % (optional)"><Input type="number" min="0" max="100" value={form.guest_profile.direct_bookings_pct} onChange={(e) => set("guest_profile", "direct_bookings_pct", e.target.value)} data-testid="input-direct-pct" /></Field>
                  <Field label="Main booking channels"><Input placeholder="e.g. Airbnb, direct, agencies" value={form.guest_profile.booking_channels} onChange={(e) => set("guest_profile", "booking_channels", e.target.value)} data-testid="input-channels" /></Field>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-12" data-testid="step-experience">
                <Field label="What makes your property exceptional?">
                  <textarea rows={5} placeholder="Tell us what makes your property different." value={form.experience.what_makes_exceptional} onChange={(e) => set("experience", "what_makes_exceptional", e.target.value)} data-testid="input-exceptional" className={`${inputCls} resize-none`} />
                </Field>
                <Field label="What services do you currently offer?"><div className="mt-3"><Chips options={SERVICES} value={form.experience.services} onToggle={(v) => toggle("experience", "services", v)} testid="services-chips" /></div></Field>

                {/* Images */}
                <div>
                  <span className="block text-[10px] tracking-[0.25em] uppercase text-ivory/50 mb-4">Property images — up to 10 (optional)</span>
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-3" data-testid="image-grid">
                    {images.map((im) => (
                      <div key={im.id} className="relative aspect-square bg-navy-800 border border-ivory/10 overflow-hidden group">
                        {im.uploading ? (
                          <div className="w-full h-full flex items-center justify-center"><Loader2 className="animate-spin text-gold/60" size={20} /></div>
                        ) : (
                          <>
                            <img src={fileUrl(im.path)} alt="" className="w-full h-full object-cover" />
                            <button type="button" onClick={() => setImages((p) => p.filter((x) => x.id !== im.id))} className="absolute top-1 right-1 bg-navy-900/80 text-ivory p-1 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`remove-image-${im.id}`}><X size={12} /></button>
                          </>
                        )}
                      </div>
                    ))}
                    {images.length < 10 && (
                      <button type="button" onClick={() => fileRef.current?.click()} className="aspect-square border border-dashed border-ivory/25 hover:border-gold text-ivory/40 hover:text-gold flex flex-col items-center justify-center gap-2 transition-colors duration-500" data-testid="upload-image-btn">
                        <Upload size={18} />
                        <span className="text-[9px] tracking-widest uppercase">Add</span>
                      </button>
                    )}
                  </div>
                  <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={(e) => uploadFiles(e.target.files)} data-testid="file-input" />
                </div>

                {/* Consent */}
                <label className="flex items-start gap-4 cursor-pointer group" data-testid="consent-label">
                  <span className={`mt-0.5 w-5 h-5 border flex items-center justify-center shrink-0 transition-colors ${consent ? "border-gold bg-gold/15" : "border-ivory/30 group-hover:border-ivory/60"}`}>
                    {consent && <Check size={13} className="text-gold" />}
                  </span>
                  <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="sr-only" data-testid="consent-checkbox" />
                  <span className="text-ivory/60 text-sm font-light leading-relaxed">
                    I agree to the processing of my personal data for the purpose of evaluating my property
                    for VillaFlow Private Collection. See our{" "}
                    <Link to="/about" className="text-gold/80 hover:text-gold underline underline-offset-4">Privacy Policy</Link>.
                  </span>
                </label>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {error && <p className="mt-8 text-gold/90 text-sm font-light" data-testid="apply-error">{error}</p>}

        {/* Nav */}
        <div className="mt-16 flex items-center justify-between">
          <button
            onClick={() => (step === 0 ? window.history.back() : setStep((s) => s - 1))}
            className="inline-flex items-center gap-2 text-ivory/50 hover:text-ivory text-xs tracking-[0.2em] uppercase transition-colors"
            data-testid="apply-back-btn"
          >
            <ArrowLeft size={15} /> Back
          </button>

          {step < STEPS.length - 1 ? (
            <button
              onClick={() => stepValid && setStep((s) => s + 1)}
              disabled={!stepValid}
              data-testid="apply-next-btn"
              className="group inline-flex items-center gap-3 border border-gold/50 hover:border-gold text-ivory hover:text-gold px-10 py-4 text-xs tracking-[0.25em] uppercase transition-colors duration-500 disabled:opacity-30"
            >
              Continue <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform duration-500" />
            </button>
          ) : (
            <button
              onClick={submit}
              disabled={submitting}
              data-testid="apply-submit-btn"
              className="group inline-flex items-center gap-3 bg-ivory hover:bg-gold text-navy px-10 py-4 text-xs tracking-[0.25em] uppercase transition-colors duration-500 disabled:opacity-50"
            >
              {submitting ? <><Loader2 size={15} className="animate-spin" /> Submitting</> : <>Submit for Consideration <ArrowRight size={15} /></>}
            </button>
          )}
        </div>
      </Container>
    </div>
  );
}
